import { useState, useCallback } from 'react';
import * as authAPI from '../api/auth';

interface SessionErrorInfo {
  message: string;
  isExpired: boolean;
  isNetworkError: boolean;
  needsReauth: boolean;
}

interface UseSessionManagerReturn {
  showSessionDialog: boolean;
  sessionError: SessionErrorInfo | null;
  handleSessionExpired: (errorInfo: Partial<SessionErrorInfo>) => void;
  handleRelogin: () => void;
  handleRetry: () => void;
  closeSessionDialog: () => void;
}

export const useSessionManager = (): UseSessionManagerReturn => {
  const [showSessionDialog, setShowSessionDialog] = useState(false);
  const [sessionError, setSessionError] = useState<SessionErrorInfo | null>(null);

  const handleSessionExpired = useCallback((errorInfo: Partial<SessionErrorInfo>) => {
    const defaultError: SessionErrorInfo = {
      message: '会话状态异常，请重新登录以继续使用系统。',
      isExpired: false,
      isNetworkError: false,
      needsReauth: true
    };

    const error = { ...defaultError, ...errorInfo };
    setSessionError(error);
    setShowSessionDialog(true);
  }, []);

  const handleRelogin = useCallback(() => {
    setShowSessionDialog(false);
    setSessionError(null);
    authAPI.clearTokens();
    // 跳转到登录页面
    window.location.href = '/login';
  }, []);

  const handleRetry = useCallback(() => {
    setShowSessionDialog(false);
    setSessionError(null);
    // 重新加载页面或者重试上一个操作
    window.location.reload();
  }, []);

  const closeSessionDialog = useCallback(() => {
    setShowSessionDialog(false);
    setSessionError(null);
  }, []);

  return {
    showSessionDialog,
    sessionError,
    handleSessionExpired,
    handleRelogin,
    handleRetry,
    closeSessionDialog
  };
};

// 创建一个全局的会话错误处理函数，可以在任何地方调用
export const createSessionErrorHandler = (handleSessionExpired: (errorInfo: Partial<SessionErrorInfo>) => void) => {
  return (refreshResult: any) => {
    if (refreshResult.expired) {
      handleSessionExpired({
        message: '您的会话已过期（超过7天未使用）。为了保护您的账户安全，请重新登录以继续使用系统。',
        isExpired: true,
        isNetworkError: false,
        needsReauth: true
      });
    } else if (refreshResult.networkError) {
      handleSessionExpired({
        message: '网络连接异常，请检查网络连接后重试。',
        isExpired: false,
        isNetworkError: true,
        needsReauth: true
      });
    } else if (refreshResult.serverError) {
      handleSessionExpired({
        message: '服务器连接失败，请稍后重试或重新登录。',
        isExpired: false,
        isNetworkError: false,
        needsReauth: true
      });
    } else {
      handleSessionExpired({
        message: '会话状态异常，请重新登录以继续使用系统。',
        isExpired: false,
        isNetworkError: false,
        needsReauth: true
      });
    }
  };
}; 